import { useGetCompany, useSetIRQ, useUpdateCompanyInherentRisk } from '@/api/company.hook'
import { IRQAnswer } from '@/gen/inventory/v1/company_service_pb'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb'

import { riskLevelLabel } from '@/const/label'

import { EventProperties, useTrackCallback } from '@/lib/analytics/events'
import { useUrlParams } from '@/lib/url/url-param.hook'

import { getInherentRisk } from '@/components/shared/risk-badge/utils'

export const useSubmitIRQ = (companyId: string, engagementId?: string) => {
  const { data: company } = useGetCompany(companyId)
  const { mutateAsync: updateCompanyInherentRisk, isPending: isUpdatingCompany } =
    useUpdateCompanyInherentRisk(companyId)
  const { urlParams } = useUrlParams()
  const trackRiskUpdate = useTrackCallback('third-party.risk.update')
  const trackIRQSubmit = useTrackCallback('third-party.irq.submit')
  const trackIRQSet = useTrackCallback('third-party.irq.set')

  const { mutateAsync: setIrq, isPending: isSettingIRQ } = useSetIRQ(companyId, engagementId)

  const submitIRQ = async (
    newRiskLevel: RiskLevel,
    suggestedRiskLevel: RiskLevel,
    comment: string,
    answers: IRQAnswer[],
    extraTrackingProps?: EventProperties,
  ) => {
    const previousRiskLevel = getInherentRisk(
      company?.company?.validatedRisk,
      company?.company?.calculatedRisk,
    )
    const wasValidated = company?.company?.validatedRisk !== undefined
    const riskChanged = newRiskLevel !== previousRiskLevel
    if (riskChanged) {
      trackRiskUpdate({
        tab: urlParams.companyDrawerTab,
        company: company?.company?.profile?.name,
        riskLevel: riskLevelLabel[newRiskLevel],
        previousRiskLevel: riskLevelLabel[previousRiskLevel],
        suggestedRiskLevel: riskLevelLabel[suggestedRiskLevel],
        companyId,
        comment,
        ...extraTrackingProps,
      })
    }
    if (!wasValidated || riskChanged) {
      await updateCompanyInherentRisk({
        id: companyId,
        riskLevel: newRiskLevel,
        comment: comment || undefined,
      })
    }

    trackIRQSubmit({
      tab: urlParams.companyDrawerTab,
      company: company?.company?.profile?.name,
      riskLevel: riskLevelLabel[newRiskLevel],
      previousRiskLevel: riskLevelLabel[previousRiskLevel],
      suggestedRiskLevel: riskLevelLabel[suggestedRiskLevel],
      id: companyId,
      comment,
      ...extraTrackingProps,
    })

    await setIrq(
      {
        companyId,
        orgCompanyEngagementId: engagementId,
        answers,
      },
      {
        onSuccess: () => {
          trackIRQSet({
            tab: urlParams.companyDrawerTab,
            company: company?.company?.profile?.name,
            companyId,
            engagementId,
            ...extraTrackingProps,
          })
        },
        onError: () => {
          trackIRQSet({
            tab: urlParams.companyDrawerTab,
            company: company?.company?.profile?.name,
            companyId,
            engagementId,
            success: false,
            ...extraTrackingProps,
          })
        },
      },
    )
  }

  return { submitIRQ, isLoading: isUpdatingCompany || isSettingIRQ }
}
